import { X, Sparkles, Target, Users, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent } from '@/components/ui/card';
import { useState } from 'react';
import { BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#6366f1', '#a855f7'];

const PersonaDemoModal = ({ isOpen, onClose, demoData, personaRole, personaIcon, darkMode }) => {
  const [showPlatforms, setShowPlatforms] = useState(false);

  if (!isOpen || !demoData) return null;

  const analysis = demoData.analysis;

  const emotionChartData = Object.entries(analysis.emotions)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5)
    .map(([name, value], idx) => ({ 
      name: name.charAt(0).toUpperCase() + name.slice(1), 
      value: Math.round(value),
      fill: COLORS[idx % COLORS.length]
    }));

  const padData = [
    { metric: 'Pleasure', value: Math.round((analysis.valence || 0.75) * 100) },
    { metric: 'Arousal', value: Math.round((analysis.arousal_level || analysis.emotional_intensity || 0.82) * 100) },
    { metric: 'Dominance', value: Math.round((analysis.dominance || 0.68) * 100) }
  ];

  // Get overall score - calculate if not present
  const overallScore = analysis.overall_score || Math.round(
    (Object.values(analysis.emotions).reduce((a, b) => a + b, 0) / Object.keys(analysis.emotions).length)
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div 
        className={`relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${
          darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
        }`}
      >
        {/* Header */}
        <div className={`sticky top-0 z-10 flex items-center justify-between p-6 border-b ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center gap-3">
            <span className="text-4xl">{personaIcon}</span>
            <div>
              <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {personaRole} Demo
              </h2>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Pre-generated analysis example
              </p>
            </div>
          </div>
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Original Content */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                📝 Sample Content
              </h3>
              <Badge variant="outline" className={`${darkMode ? 'border-blue-400 text-blue-400' : 'border-blue-600 text-blue-600'}`}>
                Demo Analysis
              </Badge>
            </div>
            <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-blue-50 border border-blue-200'}`}>
              <p className={`text-sm md:text-base ${darkMode ? 'text-gray-200' : 'text-gray-800'} italic`}>
                "{analysis.content}"
              </p>
            </div>
          </div>

          {/* Quick Results Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className={`p-4 rounded-lg text-center ${darkMode ? 'bg-gradient-to-br from-blue-900/50 to-blue-800/50 border border-blue-700' : 'bg-gradient-to-br from-blue-100 to-blue-200 border border-blue-300'}`}>
              <div className={`text-3xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                {overallScore}
              </div>
              <div className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'} mt-1`}>
                Overall Score
              </div>
            </div>
            
            <div className={`p-4 rounded-lg text-center ${darkMode ? 'bg-gradient-to-br from-purple-900/50 to-purple-800/50 border border-purple-700' : 'bg-gradient-to-br from-purple-100 to-purple-200 border border-purple-300'}`}>
              <div className={`text-2xl font-bold ${darkMode ? 'text-purple-400' : 'text-purple-700'} capitalize`}>
                {analysis.dominant_emotion}
              </div>
              <div className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'} mt-1`}>
                Dominant Emotion
              </div>
            </div>
            
            <div className={`p-4 rounded-lg text-center ${darkMode ? 'bg-gradient-to-br from-green-900/50 to-green-800/50 border border-green-700' : 'bg-gradient-to-br from-green-100 to-green-200 border border-green-300'}`}>
              <div className={`text-3xl font-bold ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                {Math.round((analysis.emotional_intensity || 0.82) * 100)}%
              </div>
              <div className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'} mt-1`}>
                Intensity
              </div>
            </div>
            
            <div className={`p-4 rounded-lg text-center ${darkMode ? 'bg-gradient-to-br from-orange-900/50 to-orange-800/50 border border-orange-700' : 'bg-gradient-to-br from-orange-100 to-orange-200 border border-orange-300'}`}>
              <div className={`text-xl font-bold ${darkMode ? 'text-orange-400' : 'text-orange-700'}`}>
                {analysis.audience_reactions?.engagement_prediction || 'Very High'}
              </div>
              <div className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'} mt-1`}>
                Engagement
              </div>
            </div>
          </div>

          {/* Emotion Breakdown */}
          <div>
            <h4 className={`text-lg font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Target className="w-5 h-5" />
              Top Emotions Detected
            </h4>
            <div className="grid gap-3">
              {Object.entries(analysis.emotions)
                .sort(([,a], [,b]) => b - a)
                .slice(0, 5)
                .map(([emotion, score]) => (
                  <div key={emotion}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className={`capitalize font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {emotion}
                      </span>
                      <span className={`font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        {Math.round(score)}%
                      </span>
                    </div>
                    <Progress value={score} className="h-2" />
                  </div>
                ))}
            </div>
          </div>

          {/* Charts */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Emotion Bar Chart */}
            <div>
              <h4 className={`text-sm font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Emotion Intensity
              </h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={emotionChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                  />
                  <YAxis 
                    tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                      border: darkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="value" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* PAD Model */}
            <div>
              <h4 className={`text-sm font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Emotional Dimensions
              </h4>
              <ResponsiveContainer width="100%" height={200}>
                <RadarChart data={padData}>
                  <PolarGrid stroke={darkMode ? '#374151' : '#e5e7eb'} />
                  <PolarAngleAxis 
                    dataKey="metric" 
                    tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                  />
                  <PolarRadiusAxis 
                    angle={90} 
                    domain={[0, 100]}
                    tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 10 }}
                  />
                  <Radar 
                    name="Score" 
                    dataKey="value" 
                    stroke="#8b5cf6" 
                    fill="#8b5cf6" 
                    fillOpacity={0.6} 
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                      border: darkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Audience Reactions */}
          <div>
            <h4 className={`text-lg font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Users className="w-5 h-5" />
              Audience Insights
            </h4>
            <div className="grid md:grid-cols-2 gap-3">
              {Object.entries(analysis.audience_reactions).map(([key, value]) => (
                <div 
                  key={key}
                  className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-gray-50 border border-gray-200'}`}
                >
                  <div className={`text-xs font-semibold uppercase mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {key.replace(/_/g, ' ')}
                  </div>
                  <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          <div>
            <h4 className={`text-lg font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <TrendingUp className="w-5 h-5" />
              AI-Powered Recommendations
            </h4>
            <div className="space-y-3">
              {analysis.recommendations.map((rec, idx) => (
                <div 
                  key={idx}
                  className={`p-4 rounded-lg flex items-start gap-3 ${darkMode ? 'bg-gradient-to-r from-indigo-900/30 to-purple-900/30 border border-indigo-700' : 'bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200'}`}
                >
                  <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${darkMode ? 'bg-indigo-600 text-white' : 'bg-indigo-600 text-white'}`}>
                    {idx + 1}
                  </div>
                  <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                    {rec}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Platform Recommendations - Collapsible */}
          {analysis.platform_recommendations && (
            <div>
              <Button
                onClick={() => setShowPlatforms(!showPlatforms)}
                variant="outline"
                className={`w-full mb-4 ${darkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'}`}
              >
                {showPlatforms ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-2" />
                    Hide Platform Recommendations
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-2" />
                    Show Platform-Specific Recommendations (6 platforms)
                  </>
                )}
              </Button>

              {showPlatforms && (
                <div className="grid md:grid-cols-2 gap-4 animate-in fade-in duration-300">
                  {Object.entries(analysis.platform_recommendations).map(([platform, data]) => (
                    <div 
                      key={platform}
                      className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h5 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {platform}
                        </h5>
                        <Badge className={`${data.score >= 85 ? 'bg-green-600' : data.score >= 75 ? 'bg-blue-600' : 'bg-orange-600'}`}>
                          {data.score}/100
                        </Badge>
                      </div>
                      <p className={`text-xs mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {data.reasoning}
                      </p>
                      <div className={`text-xs ${darkMode ? 'text-blue-400' : 'text-blue-600'} font-medium`}>
                        Best time: {data.best_time}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* CTA */}
          <div className={`mt-6 p-6 rounded-xl text-center ${darkMode ? 'bg-gradient-to-r from-blue-900/50 to-purple-900/50 border border-blue-700' : 'bg-gradient-to-r from-blue-100 to-purple-100 border border-blue-300'}`}>
            <h4 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Ready to Analyze Your Content?
            </h4>
            <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Get 25 free credits when you sign up. No credit card required!
            </p>
            <Button 
              onClick={onClose}
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Close & Start Free
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonaDemoModal;
